import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import { useAuth } from '../AuthContext';
import { usePermissions } from '../PermissionsContext';
import BradfordFactorWidget from '../BradfordFactorWidget';
import { getHomeRouteForRole } from '../utils/homeRoute';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  RateReview as ReviewIcon,
  CurrencyPound,
  School as TrainingIcon,
  Gavel as DisciplinaryIcon,
  WorkOutline as ApplicationIcon,
  Home as HomeIcon,
  AdminPanelSettings as PermissionsIcon,
  MonetizationOn as PayrollIcon,
  PregnantWoman as MaternityIcon
} from '@mui/icons-material';

const TeamDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOwner } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    pendingReviews: 0,
    trainingDue: 0,
    applications: 0,
    managerCount: 0,
    activeMaternityLeaves: 0,
    maternityActionsRequired: 0
  });
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/employees/active-count');
        if (response.data.success) {
          setStats(prev => ({
            ...prev,
            totalEmployees: response.data.count
          }));
        }

        // If owner, also get manager count
        if (isOwner()) {
          try {
            const managersResponse = await api.get('/permissions/managers');
            if (managersResponse.data.managers) {
              setStats(prev => ({
                ...prev,
                managerCount: managersResponse.data.managers.length
              }));
            }
          } catch (e) {
            console.log('Could not fetch manager count');
          }
        }

        // Fetch maternity stats
        try {
          const maternityResponse = await api.get('/maternity/dashboard');
          if (maternityResponse.data) {
            setStats(prev => ({
              ...prev,
              activeMaternityLeaves: maternityResponse.data.active_leaves || 0,
              maternityActionsRequired: maternityResponse.data.overdue_actions || 0
            }));
          }
        } catch (e) {
          console.log('Could not fetch maternity stats');
        }

        
      } catch (error) {
        console.error('Error fetching team stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isOwner]);

  const quickStats = [
    {
      title: 'Team Members',
      value: stats.totalEmployees,
      icon: <PeopleIcon />,
      color: '#2196F3',
      subtitle: 'Active employees',
      action: () => navigate('/employee_card')
    },
    {
      title: 'Reviews Due',
      value: stats.pendingReviews,
      icon: <ReviewIcon />,
      color: stats.pendingReviews > 0 ? '#FF9800' : '#4CAF50',
      subtitle: 'Pending reviews',
      action: () => navigate('/employee_reviews')
    },
    {
      title: 'Training Due',
      value: stats.trainingDue,
      icon: <TrainingIcon />,
      color: '#9C27B0',
      subtitle: 'Courses expiring',
      action: () => navigate('/submit_training')
    },
    {
      title: 'Applications',
      value: stats.applications,
      icon: <ApplicationIcon />,
      color: '#4CAF50',
      subtitle: 'New applicants',
      action: () => navigate('/employment_applications')
    }
  ];

  const actions = [
    {

      id: 'employees',
      title: 'Employee Directory',
      description: 'View, add, and manage all your team members and their details.',
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #2196F3, #1976D2)',
      path: '/employees',
      status: `${stats.totalEmployees} employees`,
      quickActions: [
        { label: 'View All Employees', path: '/employees' },
        { label: 'Add New Employee', path: '/employee_card?mode=new' },
        { label: 'Import Employees', path: '/import/employees' }
      ]
    },,
    {
      id: 'reviews',
      title: 'Performance Reviews',
      description: 'Submit reviews, view history, and track employee development.',
      icon: <ReviewIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #4CAF50, #45a049)',
      path: '/employee_reviews',
      status: `${stats.pendingReviews} pending`,
      quickActions: [
        { label: 'Review History', path: '/employee_reviews' },
        { label: 'Submit Review', path: '/submit_employee_review' },
        { label: 'Schedule Reviews', path: '/employee_reviews' }
      ]
    },
    {
      id: 'maternity',
      title: 'Maternity Tracker',
      description: 'Track maternity leave, statutory dates, KIT days, and generate compliant letters.',
      icon: <MaternityIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #ec4899, #db2777)',
      path: '/team/maternity',
      status: stats.activeMaternityLeaves > 0 ? `${stats.activeMaternityLeaves} active` : 'No active leaves',
      statusColor: stats.maternityActionsRequired > 0 ? 'warning' : 'default',
      quickActions: [
        { label: 'View All Records', path: '/team/maternity' },
        { label: 'Add New Record', path: '/team/maternity/new' },
        { label: 'Generate Letters', path: '/team/maternity' }
      ]
    },
    {
      id: 'commission',
      title: 'Commission Management',
      description: 'Set commission rates, calculate earnings, and track payments.',
      icon: <CurrencyPound sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #FF9800, #F57C00)',
      path: '/commission/dashboard',
      status: 'Manage rates',
      quickActions: [
        { label: 'Commission Dashboard', path: '/commission/dashboard' },
        { label: 'Commission Types', path: '/commission/types/new' },
        { label: 'Employee Commission', path: '/commission/dashboard' }
      ]
    },
    {
      id: 'training',
      title: 'Training & Development',
      description: 'Track training courses, certificates, and professional development.',
      icon: <TrainingIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
      path: '/submit_training',
      status: `${stats.trainingDue} due`,
      quickActions: [
        { label: 'View Training', path: '/submit_training' },
        { label: 'Add Training Record', path: '/submit_training' },
        { label: 'Training Calendar', path: '/submit_training' }
      ]
    },
    {
      id: 'disciplinary',
      title: 'Disciplinary Records',
      description: 'Manage disciplinary actions, warnings, and compliance records.',
      icon: <DisciplinaryIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #E91E63, #C2185B)',
      path: '/disciplinary',
      status: 'Manage records',
      quickActions: [
        { label: 'View Records', path: '/disciplinary' },
        { label: 'Add Record', path: '/disciplinary' },
        { label: 'Review History', path: '/disciplinary' }
      ]
    },
    {
      id: 'payroll',
      title: 'Payroll',
      description: 'Calculate staff pay, view NMW rates, and export payroll data in standard, Sage, or Xero format.',
      icon: <CurrencyPound sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #16a34a, #15803d)',
      path: '/payroll-calculator',
      status: 'Owner only',
      quickActions: [
        { label: 'Payroll Calculator', path: '/payroll-calculator' },
        { label: 'Export Payroll CSV', path: '/payroll-export' },
        { label: 'Pay Rates', path: '/payroll-calculator' }
      ]
    },
    {
      id: 'applications',
      title: 'Job Applications',
      description: 'Manage recruitment, review applications, and hire new staff.',
      icon: <ApplicationIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #00bcd4, #0097a7)',
      path: '/employment_applications',
      status: `${stats.applications} new`,
      quickActions: [
        { label: 'View Applications', path: '/employment_applications' },
        { label: 'Application Form', path: '/employment_applications' },
        { label: 'Recruitment', path: '/employment_applications' }
      ]
    }
  ];

  const ownerActions = [
    {
      id: 'manager-permissions',
      title: 'Manager Permissions',
      description: 'Control what your managers can access. Toggle permissions for each section.',
      icon: <PermissionsIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #667eea, #764ba2)',
      path: '/settings/manager-permissions',
      status: `${stats.managerCount} managers`,
      quickActions: [
        { label: 'Configure Permissions', path: '/settings/manager-permissions' },
        { label: 'View Manager List', path: '/settings/manager-permissions' }
      ]
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 2
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate(getHomeRouteForRole(user?.role))}
            sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Back to Hub
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Team Management
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Manage employees, reviews, training, and recruitment
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {quickStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #e9ecef',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
                }}
                onClick={stat.action}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${stat.color}20`, color: stat.color }}>{stat.icon}</Avatar>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {stats.maternityActionsRequired > 0 && (
          <Card
            elevation={0}
            sx={{
              border: '2px solid #ec4899',
              background: 'linear-gradient(135deg, #fdf2f8 0%, #fff 100%)',
              mb: 4,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(236, 72, 153, 0.2)' }
            }}
            onClick={() => navigate('/team/maternity')}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 48, height: 48, background: 'linear-gradient(135deg, #ec4899, #db2777)' }}>
                  <MaternityIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color="#db2777">
                    Maternity Actions Required
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You have {stats.maternityActionsRequired} overdue action{stats.maternityActionsRequired !== 1 ? 's' : ''} requiring attention
                  </Typography>
                </Box>
                <Chip
                  label="Review Now"
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
            Absence Monitoring
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <BradfordFactorWidget onClick={() => navigate('/bradford-factor')} />
            </Grid>
          </Grid>
        </Box>

        {isOwner() && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PermissionsIcon /> Owner Settings
            </Typography>
            <Grid container spacing={3}>
              {ownerActions.map((action) => (
                <Grid item xs={12} md={6} key={action.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '2px solid #667eea',
                      background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)',
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ width: 56, height: 56, background: action.iconBg }}>{action.icon}</Avatar>
                        <Chip
                          label={action.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white'
                          }}
                        />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{action.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{action.description}</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>QUICK ACTIONS:</Typography>
                        {action.quickActions.map((qa, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(qa.path); }}
                            sx={{
                              display: 'block',
                              textAlign: 'left',
                              textTransform: 'none',
                              mb: 0.5,
                              justifyContent: 'flex-start',
                              color: '#667eea',
                              '&:hover': { bgcolor: '#667eea10' }
                            }}
                          >
                            → {qa.label}
                          </Button>
                        ))}
                      </Box>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate(action.path)}
                        sx={{
                          background: action.iconBg,
                          textTransform: 'none',
                          py: 1.5,
                          fontWeight: 600,
                          '&:hover': { opacity: 0.9 }
                        }}
                      >
                        Open {action.title}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#2c3e50' }}>
          What would you like to do?
        </Typography>

        <Grid container spacing={3}>
          {actions.map((action) => (
            <Grid item xs={12} md={6} key={action.id}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #e9ecef',
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, background: action.iconBg }}>{action.icon}</Avatar>
                    <Chip
                      label={action.status}
                      size="small"
                      color={action.statusColor || 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{action.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{action.description}</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>QUICK ACTIONS:</Typography>
                    {action.quickActions.map((qa, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(qa.path); }}
                        sx={{
                          display: 'block',
                          textAlign: 'left',
                          textTransform: 'none',
                          mb: 0.5,
                          justifyContent: 'flex-start',
                          color: '#667eea',
                          '&:hover': { bgcolor: '#667eea10' }
                        }}
                      >
                        → {qa.label}
                      </Button>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(action.path)}
                    sx={{
                      background: action.iconBg,
                      textTransform: 'none',
                      py: 1.5,
                      fontWeight: 600,
                      '&:hover': { opacity: 0.9 }
                    }}
                  >
                    Open {action.title}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TeamDashboard;